import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { useFeature } from 'src/core/common/hooks/useFeature';
import FEATURES from 'src/core/constants/features';

import { type UseCustomFieldsData } from './useCustomFieldsData';
import { type AnalyticalSplitFormValue } from '../../models/analyticalSplit';
import { type CustomFieldEligibleType } from '../../models/customFieldAssociation';

export interface UseAnalyticalSplitDisplaySettingsInputs {
  eligibleType: CustomFieldEligibleType;
  getCustomFieldData: UseCustomFieldsData;
  analyticalSplitFormValue?: AnalyticalSplitFormValue;
}

export interface UseAnalyticalSplitDisplaySettingsOutputs {
  isAnalyticalSplitEnabled: boolean;
  expenseCategoryCustomFieldIdForAnalyticalSplit?: string;
  isExpenseCategoryRequiredForAnalyticalSplit?: boolean;
  displayCostCenterInAnalyticalSplit?: boolean;
  displayExpenseCategoryInAnalyticalSplit?: boolean;
  splittedCustomFieldsIds?: string[];
}

// this hook returns the display settings for the analyticalSplit part of the BudgetTrackingFormFields component
// they depend on analyticalSplit x custom fields x team x cost center
// for example custom fields and expense category are only displayed if not in the split
export const useAnalyticalSplitDisplaySettings = ({
  eligibleType,
  getCustomFieldData,
  analyticalSplitFormValue,
}: UseAnalyticalSplitDisplaySettingsInputs): UseAnalyticalSplitDisplaySettingsOutputs => {
  const isAnalyticalSplitEnabled = useFeature(FEATURES.TMP_ANALYTICAL_SPLITS);

  if (!isAnalyticalSplitEnabled) {
    return {
      isAnalyticalSplitEnabled: false,
    };
  }

  // Expense category
  // note: here we get the information about expense category
  // irrespective of whether the cost center has been set on the "root" values
  // as it may be present in the split but not at the root level
  // hence the call to customFieldsQueryState with an empty object
  const customFieldDataForAnalyticalSplit =
    getCustomFieldData.isCustomFieldsEnabled
      ? unwrapQuery(getCustomFieldData.customFieldsQueryState)?.(
          eligibleType,
          {},
        )
      : undefined;

  const expenseCategoryCustomFieldIdForAnalyticalSplit =
    customFieldDataForAnalyticalSplit?.isExpenseCategoryEnabled &&
    customFieldDataForAnalyticalSplit?.expenseCategoryField?.is_splittable
      ? customFieldDataForAnalyticalSplit.expenseCategoryField.id
      : undefined;

  const isExpenseCategoryRequiredForAnalyticalSplit =
    !!expenseCategoryCustomFieldIdForAnalyticalSplit &&
    customFieldDataForAnalyticalSplit?.isExpenseCategoryEnabled &&
    customFieldDataForAnalyticalSplit?.isExpenseCategoryRequired;

  if (!analyticalSplitFormValue?.activate) {
    return {
      isAnalyticalSplitEnabled,
      expenseCategoryCustomFieldIdForAnalyticalSplit,
      isExpenseCategoryRequiredForAnalyticalSplit,
    };
  }

  const displayCostCenterInAnalyticalSplit =
    analyticalSplitFormValue?.dimensions.splitOnCostCenter;

  const displayExpenseCategoryInAnalyticalSplit =
    !!expenseCategoryCustomFieldIdForAnalyticalSplit &&
    analyticalSplitFormValue?.dimensions.splitOnCustomFieldsIds?.includes(
      expenseCategoryCustomFieldIdForAnalyticalSplit,
    );

  const splittedCustomFieldsIds =
    analyticalSplitFormValue?.dimensions.splitOnCustomFieldsIds?.filter(
      (id) => id !== expenseCategoryCustomFieldIdForAnalyticalSplit,
    );

  return {
    isAnalyticalSplitEnabled,
    expenseCategoryCustomFieldIdForAnalyticalSplit,
    isExpenseCategoryRequiredForAnalyticalSplit,
    displayCostCenterInAnalyticalSplit,
    displayExpenseCategoryInAnalyticalSplit,
    splittedCustomFieldsIds,
  };
};
